/**
 * The ⤢ that pops the case file out — labels and the open/closed state.
 *
 * Scott, 2026-09-08: "Looks like it needs to be maximizable" … and then, on a
 * first cut that grew the case file in place: "The expanse should be a full
 * pop-out modal."
 *
 * So the state lives here rather than in any one host, because the same ⤢
 * appears on both surfaces that show a cramped case file — the Cases-tab
 * investigations panel (400px wide) and the case file embedded in the triage
 * board (capped at 44vh) — and both open the SAME CaseFileModal.
 *
 * Deliberately NOT remembered between sessions. A width is a preference worth
 * persisting; "a modal is open" is not — a case file that pops itself open
 * every time the tab loads would ambush the reader. It opens on the ⤢ and
 * closes on ✕, the backdrop, or Escape.
 */
import { useCallback, useState } from 'react';

/** Button label, so the control reads the same on every surface. */
export const CASE_FILE_POPOUT_LABEL = 'Expand case file';

/** Tooltip — says what it does, not what it is. */
export const CASE_FILE_POPOUT_HINT = 'Open the case file full screen';

export interface CaseFilePopoutState {
  /** True while the pop-out modal is showing. */
  popped: boolean;
  open: () => void;
  close: () => void;
}

export function useCaseFilePopout(): CaseFilePopoutState {
  const [popped, setPopped] = useState(false);
  const open = useCallback(() => setPopped(true), []);
  const close = useCallback(() => setPopped(false), []);
  return { popped, open, close };
}
